import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PortraitPlaceholder, { EditorialImage } from "@/components/Placeholder";
import { PullQuotePrimary, PullQuoteInline } from "@/components/PullQuote";
import EmailSignup from "@/components/EmailSignup";
import { QUOTES } from "@/lib/quotes";
import { useSEO, SEO } from "@/lib/seo";

const IMG = {
    stage: "/images/stage.jpg",
    curtain: "/images/curtain.jpg",
    warmShadow: "/images/warm-shadow.jpg",
    microphone: "/images/microphone.jpg",
};

const CHAPTERS = [
    {
        n: "I.",
        eyebrow: "Before",
        title: "The singer I was before cancer",
        body: [
            "For most of my life, music was the room I felt most at home in. The stage was not a performance — it was a way of remembering who I was.",
            "I had a career I loved. I had a voice that knew where to go. I had a life that, while imperfect, still felt unmistakably mine.",
        ],
        dark: false,
        photo: {
            src: "/images/chrissa-early-career.jpg",
            alt: "Chrissa Valanti laughing at a recording mixing console, wearing a black knitted beanie, at Sing Sing Studios Melbourne.",
            position: "center",
        },
    },
    {
        n: "II.",
        eyebrow: "The Diagnosis",
        title: "The diagnosis that changed everything",
        body: [
            "I will not describe the words a doctor uses when they tell you what they have to tell you. You either know them already or you do not need to.",
            "What I will say is this: in one afternoon, the version of my future that I had quietly been walking toward simply stopped existing.",
        ],
        quote: QUOTES.adversity,
        dark: true,
        portrait: null,
        image: IMG.warmShadow,
    },
    {
        n: "III.",
        eyebrow: "Surviving",
        title: "Surviving but no longer feeling like myself",
        body: [
            "I survived. People said the word as if it should have been enough.",
            "But surviving and feeling alive are not always the same thing. I was here, breathing, on the other side of treatment — and yet the woman I had been before the diagnosis felt very far away.",
        ],
        quote: QUOTES.survival,
        dark: false,
    },
    {
        n: "IV.",
        eyebrow: "Silence",
        title: "Why I walked away from music",
        body: [
            "My body had changed. My energy had changed. My confidence had changed.",
            "Cancer gave me a reason to hide. I took it. For a long time, I told myself it was wisdom. It was not wisdom. It was fear, dressed in a more acceptable outfit.",
        ],
        dark: true,
        portrait: "Empty stage",
        image: IMG.curtain,
    },
    {
        n: "V.",
        eyebrow: "Again",
        title: "My second stem-cell transplant",
        body: [
            "The second time, I knew what I was walking into. I knew the rooms. I knew the corridors. I knew the slow weight of a body learning to trust itself a second time.",
            "There are details I will not share publicly. There are some that belong only to the people who sat with me.",
        ],
        quote: QUOTES.bodyWorth,
        dark: false,
        photo: {
            src: "/images/chrissa-hospital-transplant.jpg",
            alt: "Chrissa Valanti in hospital during her second stem-cell transplant, before hair loss.",
            position: "center 30%",
        },
    },
    {
        n: "VI.",
        eyebrow: "Loss",
        title: "Losing my father",
        body: [
            "While I was in hospital, my father died.",
            "Grief inside recovery is its own kind of country. You do not get to leave one room before entering another.",
        ],
        dark: true,
        image: IMG.warmShadow,
    },
    {
        n: "VII.",
        eyebrow: "His Wish",
        title: "His final wish",
        body: [
            "Among his final words to me was a reminder of how much he loved hearing me sing. His wish was simple: he wanted me to find my voice again.",
            "I could not honour that wish by continuing to disappear.",
        ],
        quote: QUOTES.father,
        dark: false,
    },
    {
        n: "VIII.",
        eyebrow: "Return",
        title: "Returning to the stage",
        body: [
            "The first time I stood in front of a microphone again, I was frightened. I was changed. I did not feel completely ready.",
            "But when I began to sing, something inside me returned. Not the old version of me — a different one, who had been through things, and was still here.",
        ],
        quote: QUOTES.survivalIdentity,
        dark: true,
        photo: {
            src: "/images/chrissa-return-stage.jpg",
            alt: "Chrissa Valanti returning to the stage and reclaiming her voice after her stem-cell transplant.",
            position: "center 20%",
        },
    },
    {
        n: "IX.",
        eyebrow: "Founder",
        title: "Becoming a founder",
        body: [
            "I never planned to build a technology company. I never planned to start a soap business with my son. I planned a different life — and that life did not survive intact.",
            "What did survive was the question: what can I make from what life has taught me?",
        ],
        dark: false,
        photo: {
            src: "/images/chrissa-monthly-treatment.jpg",
            alt: "Chrissa Valanti receiving ongoing monthly treatment during recovery.",
            position: "center 30%",
        },
    },
    {
        n: "X.",
        eyebrow: "Now",
        title: "What I am building now",
        body: [
            "Valanti Labs. ALATI. Music. They look like three different things. To me, they are three answers to the same question.",
            "Each began before I felt completely healed, fearless or ready. Each began from where I was.",
        ],
        quote: QUOTES.story,
        dark: true,
        photo: {
            src: "/images/chrissa-beach.jpg",
            alt: "Chrissa Valanti by the sea, one year on from her second stem-cell transplant.",
            position: "center",
        },
    },
    {
        n: "XI.",
        eyebrow: "To You",
        title: "An invitation to the reader",
        body: [
            "If part of you has gone quiet, I understand. If you have postponed something that used to feel essential, I understand that too.",
            "I cannot tell you what your next beginning should look like. I can only show you what mine looks like while it is being built.",
        ],
        dark: false,
    },
];

export default function Journey() {
    useSEO(SEO.journey);
    return (
        <main>
            {/* Hero */}
            <section className="bg-charcoal text-ivory pt-36 md:pt-44 pb-20 md:pb-28">
                <div className="container-editorial">
                    <p className="eyebrow text-gold/90">My Journey</p>
                    <h1 className="display-1 mt-8 max-w-4xl text-ivory">
                        A life that nearly ended <span className="italic text-gold/95">— and what began afterwards.</span>
                    </h1>
                    <p className="mt-10 body-lead text-ivory/70 max-w-2xl">
                        This is not a medical history. It is the story of how I lost trust in my body, my future and my
                        voice — and how I began rebuilding all three.
                    </p>
                </div>
            </section>

            {/* Chapters */}
            {CHAPTERS.map((c, i) => (
                <section
                    key={c.n}
                    className={`${c.dark ? "bg-charcoal text-ivory" : "bg-ivory text-charcoal"}`}
                >
                    <div className="container-editorial py-20 md:py-28">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                            <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
                                <p className={`font-serif text-6xl md:text-7xl ${c.dark ? "text-gold/40" : "text-oxblood/30"}`}>
                                    {c.n}
                                </p>
                                <p className={`eyebrow mt-4 ${c.dark ? "text-gold/90" : "text-oxblood"}`}>
                                    Chapter {i + 1} — {c.eyebrow}
                                </p>
                                <h2 className={`display-3 mt-6 ${c.dark ? "text-ivory" : "text-charcoal"}`}>
                                    {c.title}
                                </h2>
                            </div>

                            <div className="lg:col-span-8 space-y-6 body-lead">
                                {c.body.map((p, idx) => (
                                    <p key={idx} className={c.dark ? "text-ivory/80" : "text-charcoal/80"}>
                                        {p}
                                    </p>
                                ))}

                                {c.photo && (
                                    <figure className="pt-6">
                                        <div className="relative overflow-hidden aspect-[16/10] bg-charcoal-soft max-w-[640px]">
                                            <img
                                                src={c.photo.src}
                                                alt={c.photo.alt}
                                                loading="lazy"
                                                className="absolute inset-0 w-full h-full object-cover"
                                                style={{ objectPosition: c.photo.position || "center" }}
                                            />
                                        </div>
                                    </figure>
                                )}
                                {c.image && (
                                    <div className="pt-6">
                                        <EditorialImage src={c.image} alt={c.portrait || ""} aspect="aspect-[16/9]" />
                                    </div>
                                )}
                                {c.portrait && !c.image && !c.photo && (
                                    <div className="pt-6">
                                        <PortraitPlaceholder label={c.portrait} aspect="aspect-[16/10]" dark={c.dark} />
                                    </div>
                                )}

                                {c.quote && (
                                    <div className="pt-6">
                                        <PullQuoteInline dark={c.dark}>{c.quote}</PullQuoteInline>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* Closing statement */}
            <PullQuotePrimary dark>{QUOTES.transformationFirstPerson}</PullQuotePrimary>

            {/* Signup */}
            <section className="bg-ivory text-charcoal">
                <div className="container-narrow py-24 md:py-32 text-center">
                    <p className="eyebrow text-oxblood">Continue with me</p>
                    <h2 className="display-2 mt-6">
                        Receive the next chapter as it happens.
                    </h2>
                    <p className="mt-6 body-lead text-charcoal/70">
                        Personal letters from Chrissa — sent occasionally, never noisily.
                    </p>
                    <div className="mt-10 text-left">
                        <EmailSignup source="journey" />
                    </div>
                    <div className="mt-12">
                        <Link to="/building" className="btn-secondary">
                            See What I'm Building <ArrowRight size={16} strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
